import * as React from "react";
import { Alert, Box, Button, Card, Divider, Skeleton, Stack, Typography } from "@mui/material";

import type { RateCardItem } from "@/hooks/api/use-rate-card";

import { formatAmount, formatPrice } from "../../rate-card/format";
import type { Estimate } from "../estimate";
import { SELECTED_TINT } from "./section-card";

interface EstimatePanelProps {
	estimate: Estimate | null;
	card: RateCardItem[] | undefined;
	cardError: boolean;
	/** The line that just changed, shown tinted for a moment. */
	freshKey: string | null;
	submitting: boolean;
	/** What submitting is doing right now, e.g. "Uploading the lead list". */
	step: string | null;
	draftSaved: boolean;
	hasErrors: boolean;
	onSubmit: () => void;
}

/** "Talk time is billed monthly: SGD 0.30 per minute, plus telco carriage at cost, by market." */
function usageNote(card: RateCardItem[]): string | null {
	const lines = card.filter((item) => item.section === "usage");
	if (lines.length === 0) return null;
	const parts = lines.map((item, index) => {
		const price =
			item.amount === null
				? (item.priceNote ?? "price on request").replace(/^./, (c) => c.toLowerCase())
				: formatPrice(item);
		return index === 0 ? price : `plus ${item.name.toLowerCase()} ${price}`;
	});
	return `Talk time is billed monthly: ${parts.join(", ")}.`;
}

/** The running total beside the form, like a basket: every market, agent and service the client adds. */
export function EstimatePanel({
	estimate,
	card,
	cardError,
	freshKey,
	submitting,
	step,
	draftSaved,
	hasErrors,
	onSubmit,
}: EstimatePanelProps): React.JSX.Element {
	const count = estimate?.lines.length ?? 0;

	return (
		<Stack spacing={1.5}>
			<Card component="aside" aria-label="Your estimate">
				<Stack
					direction="row"
					sx={{ justifyContent: "space-between", alignItems: "baseline", px: 3, pt: 2.5, pb: 1.5 }}
				>
					<Typography variant="h6">Your estimate</Typography>
					{count > 0 ? (
						<Typography variant="body2" color="text.secondary">
							{count} {count === 1 ? "item" : "items"}
						</Typography>
					) : null}
				</Stack>

				<Box aria-live="polite" sx={{ px: 1.5 }}>
					{card === undefined && !cardError ? (
						<Stack spacing={1} sx={{ px: 1.5, pb: 1 }}>
							<Skeleton height={36} />
							<Skeleton height={36} />
						</Stack>
					) : cardError ? (
						<Alert severity="info" sx={{ mx: 1.5, mb: 1 }}>
							The rate card isn’t available right now. Send the request anyway and we’ll quote you.
						</Alert>
					) : count === 0 ? (
						<Typography variant="body2" color="text.secondary" sx={{ px: 1.5, pb: 1.5 }}>
							Add a market to see your estimate.
						</Typography>
					) : (
						estimate!.lines.map((line) => (
							<Stack
								key={line.key}
								direction="row"
								spacing={2}
								sx={{
									justifyContent: "space-between",
									px: 1.5,
									py: 1,
									borderRadius: 1.25,
									bgcolor: line.key === freshKey ? SELECTED_TINT : "transparent",
									transition: "background-color 600ms ease",
								}}
							>
								<Box sx={{ minWidth: 0 }}>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{line.label}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{line.detail}
									</Typography>
								</Box>
								<Typography
									variant="body2"
									sx={{ fontWeight: 600, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
								>
									{line.amount === null
										? (line.priceNote ?? "Price on request")
										: formatAmount(estimate!.currency, line.amount)}
								</Typography>
							</Stack>
						))
					)}
				</Box>

				<Box sx={{ px: 3, pb: 3 }}>
					{estimate && count > 0 ? (
						<>
							<Divider sx={{ my: 1.5 }} />
							<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
								<Typography variant="body2" color="text.secondary">
									One-off total
								</Typography>
								<Typography variant="h4" component="p" sx={{ fontVariantNumeric: "tabular-nums" }}>
									{formatAmount(estimate.currency, estimate.total)}
								</Typography>
							</Stack>
							<Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
								Excludes GST. {card ? usageNote(card) : null}
							</Typography>
						</>
					) : null}

					{hasErrors ? (
						<Alert severity="error" sx={{ mt: 2 }}>
							Some details are missing. The sections that need them are marked.
						</Alert>
					) : null}
					{draftSaved && !submitting ? (
						<Alert severity="info" sx={{ mt: 2 }}>
							Your request is saved as a draft. Submit again to finish.
						</Alert>
					) : null}

					<Button fullWidth size="large" variant="contained" onClick={onSubmit} disabled={submitting} sx={{ mt: 2 }}>
						{submitting ? `${step ?? "Sending"}…` : "Submit request"}
					</Button>
					<Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, textAlign: "center" }}>
						Nothing is charged now. We confirm the quote with you first.
					</Typography>
				</Box>
			</Card>
		</Stack>
	);
}
